/**
 * Asking the room.
 *
 * "What are we playing?" and "when is everyone free?" are the two questions a
 * group asks every week, and both were being answered by six people typing
 * over each other. A poll is a message rather than a thing beside the chat:
 * it sits where it was asked, scrolls with everything else, and when it
 * closes the answer stays in the log where the decision was made.
 *
 * Votes are counts, never a list of who picked what - the point is to get to
 * an answer, not to have an argument about who wanted Minecraft.
 */

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, socket } from '@/lib/api';
import type { Message, Poll as PollData } from '@/lib/types';
import { cn } from '@/lib/cn';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { Button, Field, Modal, useToast } from './ui';
import { Switch } from './Switch';
import { GameMark } from './GameMark';

/** "closes in 3h", or "closed". */
function closesText(poll: PollData): string {
  if (poll.closed) return 'closed';
  if (!poll.closesAt) return 'open';
  const left = poll.closesAt - Date.now();
  if (left <= 0) return 'closed';
  const mins = Math.round(left / 60000);
  if (mins < 60) return 'closes in ' + mins + 'm';
  const hours = Math.round(mins / 60);
  return hours < 48 ? 'closes in ' + hours + 'h' : 'closes in ' + Math.round(hours / 24) + 'd';
}

export function PollCard({ msg }: { msg: Message }) {
  const me = useSession((s) => s.me);
  const toast = useToast();
  const queryClient = useQueryClient();
  const [poll, setPoll] = useState<PollData | undefined>(msg.poll);
  const [busy, setBusy] = useState(false);

  /* Everybody in the room sees a vote land, not only the person who cast it. */
  useEffect(() => setPoll(msg.poll), [msg.poll]);
  useEffect(() => socket.on('poll:updated', (data: { id: string; poll: PollData }) => {
    if (data.id !== msg.id) return;
    setPoll((old) => (old ? {
      ...data.poll,
      /* The server's broadcast has no viewer, so keep what this one picked. */
      options: data.poll.options.map((o, i) => ({ ...o, mine: old.options[i]?.mine ?? false }))
    } : data.poll));
  }), [msg.id]);

  if (!poll) return null;

  const total = poll.total;
  const closed = poll.closed || (!!poll.closesAt && poll.closesAt <= Date.now());
  const mine = poll.options.some((o) => o.mine);

  async function vote(i: number) {
    if (busy || closed) return;
    setBusy(true);
    /* The bar moves now; the server confirms a moment later. */
    setPoll((old) => {
      if (!old) return old;
      const options = old.options.map((o, idx) => {
        const was = o.mine;
        const picked = old.multi ? (idx === i ? !was : was) : idx === i;
        return { ...o, mine: picked, votes: o.votes + (picked && !was ? 1 : !picked && was ? -1 : 0) };
      });
      return { ...old, options, total: Math.max(old.total, options.some((o) => o.mine) ? old.total + (mine ? 0 : 1) : old.total) };
    });
    try {
      const choices = poll!.multi
        ? poll!.options.map((o, idx) => ({ o, idx })).filter(({ o, idx }) => (idx === i ? !o.mine : o.mine)).map(({ idx }) => idx)
        : [i];
      const res = await api.post<{ poll: PollData }>('/api/polls/' + msg.id + '/vote', { choices });
      setPoll(res.poll);
    } catch (err) {
      toast((err as Error).message, 'bad');
      void queryClient.invalidateQueries({ queryKey: ['messages'] });
    } finally {
      setBusy(false);
    }
  }

  async function close() {
    setBusy(true);
    try {
      const res = await api.post<{ poll: PollData }>('/api/polls/' + msg.id + '/close', {});
      setPoll(res.poll);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  const winner = closed ? Math.max(...poll.options.map((o) => o.votes)) : -1;

  return (
    <div className="mt-1 max-w-[440px] rounded-[calc(16px*var(--radius-mul,1))] bg-surface-2 p-3 shadow-[inset_0_0_0_1px_var(--line-soft)]">
      <div className="flex items-start gap-2">
        <Icon name="chart" size={16} className="mt-0.5 flex-none text-accent" />
        <p className="m-0 min-w-0 flex-1 text-[14.5px] font-bold text-ink">{poll.question}</p>
      </div>

      <ul className="m-0 mt-2.5 flex list-none flex-col gap-1.5 p-0">
        {poll.options.map((o) => {
          const share = total ? Math.round((o.votes / total) * 100) : 0;
          const won = closed && o.votes === winner && o.votes > 0;
          return (
            <li key={o.i}>
              <button
                type="button"
                disabled={busy || closed}
                aria-pressed={o.mine}
                onClick={() => void vote(o.i)}
                className={cn(
                  'relative flex w-full items-center gap-2 overflow-hidden rounded-[calc(10px*var(--radius-mul,1))] px-2.5 py-2 text-left outline-none',
                  'transition-[box-shadow,background-color] duration-[160ms] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                  closed ? 'cursor-default' : 'hover:bg-surface-3',
                  o.mine ? 'shadow-[inset_0_0_0_1.5px_var(--accent)]' : 'shadow-[inset_0_0_0_1px_var(--line-soft)]'
                )}
              >
                {/* The bar is behind the text, not beside it - a row of
                    numbers is harder to read at a glance than a length. */}
                <span
                  aria-hidden
                  className={cn('absolute inset-y-0 left-0 transition-[width] duration-[400ms] ease-[var(--ease-out)]', won ? 'bg-[color-mix(in_srgb,var(--good)_22%,transparent)]' : 'bg-accent-soft')}
                  style={{ width: (mine || closed ? share : 0) + '%' }}
                />
                <span className={cn('relative z-[1] min-w-0 flex-1 truncate text-[13.5px] font-semibold', o.mine ? 'text-accent' : 'text-ink')}>
                  {o.text}
                </span>
                {mine || closed ? (
                  <span className="relative z-[1] flex-none text-[12px] font-bold text-dim tabular-nums">{share}%</span>
                ) : null}
                {o.mine ? <Icon name="check" size={13} className="relative z-[1] flex-none text-accent" /> : null}
              </button>
            </li>
          );
        })}
      </ul>

      <p className="m-0 mt-2 flex items-center gap-2 text-[11.5px] text-mute">
        <span>{total} {total === 1 ? 'vote' : 'votes'}</span>
        <span aria-hidden>·</span>
        <span>{closesText(poll)}</span>
        {poll.multi ? <><span aria-hidden>·</span><span>pick as many as you like</span></> : null}
        {!closed && msg.userId === me?.id ? (
          <>
            <span className="flex-1" />
            <button type="button" onClick={() => void close()} disabled={busy} className="font-semibold text-mute transition-colors hover:text-ink">
              Close it
            </button>
          </>
        ) : null}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------ asking */

/** The next seven evenings, for "when is everyone free?". */
function evenings(): string[] {
  const out: string[] = [];
  for (let i = 0; i < 5; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    out.push(d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' }) + ' evening');
  }
  return out;
}

export function NewPoll({ scope, channelId, onClose }: {
  scope: 'party' | 'crew' | 'group' | 'dm';
  channelId: string;
  onClose: () => void;
}) {
  const catalog = useSession((s) => s.catalog);
  const me = useSession((s) => s.me);
  const toast = useToast();
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  const [multi, setMulti] = useState(false);
  const [hours, setHours] = useState(24);
  const [busy, setBusy] = useState(false);

  const set = (i: number, text: string) => setOptions((list) => list.map((o, idx) => (idx === i ? text : o)));

  /* Two shapes that cover most of what a group actually asks. */
  function preset(kind: 'games' | 'when') {
    if (kind === 'games') {
      const mine = (me?.games ?? []).map((g) => catalog?.games.find((x) => x.id === g.id)?.name).filter(Boolean) as string[];
      setQuestion('What are we playing?');
      setOptions(mine.length >= 2 ? mine.slice(0, 5) : ['Valorant', 'Minecraft', 'Something else']);
      setMulti(true);
    } else {
      setQuestion('When is everyone free?');
      setOptions(evenings());
      setMulti(true);
    }
  }

  async function send() {
    const clean = options.map((o) => o.trim()).filter(Boolean);
    if (!question.trim()) { toast('Give the poll a question.', 'bad'); return; }
    if (clean.length < 2) { toast('A poll needs at least two answers.', 'bad'); return; }
    setBusy(true);
    try {
      await api.post('/api/polls', { scope, channelId, question: question.trim(), options: clean, multi, hours });
      onClose();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Ask the room"
      subtitle="A poll lands in the conversation, and everyone can change their answer until it closes."
      onClose={onClose}
      footer={
        <>
          <Button kind="outline" onClick={onClose}>Cancel</Button>
          <Button data-primary kind="primary" disabled={busy} onClick={() => void send()}>{busy ? 'Posting…' : 'Post the poll'}</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" kind="outline" icon={<GameMark id={me?.games?.[0]?.id ?? 'valorant'} size={16} plain />} onClick={() => preset('games')}>
            What are we playing?
          </Button>
          <Button size="sm" kind="outline" icon={<Icon name="calendar" size={15} />} onClick={() => preset('when')}>
            When is everyone free?
          </Button>
        </div>

        <Field label="Question">
          <input
            autoFocus
            value={question}
            onChange={(e) => setQuestion(e.target.value.slice(0, 120))}
            placeholder="What are we playing tonight?"
            className="h-[var(--h-md)] w-full rounded-[calc(12px*var(--radius-mul,1))] border border-line bg-surface-1 px-3 text-[14px] text-ink outline-none transition-colors placeholder:text-ghost focus:border-accent"
          />
        </Field>

        <div>
          <span className="mb-2 block text-[13.5px] font-semibold text-ink">Answers</span>
          <div className="flex flex-col gap-1.5">
            {options.map((o, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <input
                  value={o}
                  onChange={(e) => set(i, e.target.value.slice(0, 60))}
                  placeholder={'Answer ' + (i + 1)}
                  className="h-10 min-w-0 flex-1 rounded-[calc(10px*var(--radius-mul,1))] border border-line bg-surface-1 px-3 text-[13.5px] outline-none transition-colors placeholder:text-ghost focus:border-accent"
                />
                {options.length > 2 ? (
                  <button
                    type="button"
                    aria-label={'Remove answer ' + (i + 1)}
                    onClick={() => setOptions((list) => list.filter((_, idx) => idx !== i))}
                    className="grid h-9 w-9 flex-none place-items-center rounded-[10px] text-mute transition-colors hover:bg-surface-2 hover:text-ink"
                  >
                    <Icon name="close" size={14} />
                  </button>
                ) : null}
              </div>
            ))}
          </div>
          {options.length < 8 ? (
            <button
              type="button"
              onClick={() => setOptions((list) => [...list, ''])}
              className="mt-2 text-[13px] font-semibold text-accent transition-colors hover:text-accent-hi"
            >
              Add another
            </button>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <Switch checked={multi} onChange={setMulti} label="Let people pick more than one" />
        </div>

        <div>
          <span className="mb-2 block text-[13.5px] font-semibold text-ink">Closes</span>
          <div className="flex flex-wrap gap-1.5">
            {[[1, 'In an hour'], [8, 'In 8 hours'], [24, 'Tomorrow'], [72, 'In 3 days'], [0, 'When I close it']].map(([h, label]) => {
              const on = hours === h;
              return (
                <button
                  key={String(h)}
                  type="button"
                  onClick={() => setHours(h as number)}
                  aria-pressed={on}
                  className={cn(
                    'h-9 rounded-full px-3.5 text-[12.5px] font-semibold outline-none transition-colors',
                    on ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-dim hover:bg-surface-3 hover:text-ink'
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
}
